import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Library, User, Clock, ChevronLeft, Star,
} from "lucide-react";
import Header from "@/components/homepage/Header";
import { Footer } from "@/components/homepage/Footer";
import { useKnowledgeList } from "@/hooks/useKnowledge";
import {
  KNOWLEDGE_CATEGORIES,
  CATEGORY_LABEL,
  CATEGORY_ICON,
  COMING_SOON_CATEGORIES,
} from "@/constants/knowledgeCategories";

/**
 * Knowledge Library — Executive learning stories.
 *
 * Consolidated learning surface: person & company stories live today.
 * Future categories (strategy · investment · economy · leadership ·
 * innovation · historical mistakes · crises · acquisitions · market
 * lessons) are declared in the shared registry so the taxonomy is
 * ready as content lands.
 *
 * Editorial layout · matches the design system:
 *   • Restrained hero (smaller than Opportunities hero to preserve
 *     visual dominance of the investment surface).
 *   • Horizontally-scrollable category tabs.
 *   • Featured cards row + regular grid.
 *   • Roadmap band showing what's coming next.
 */

/* Category icon/label lookups come from the shared registry so the
   admin editor, list filter, and public library stay in sync. */

export default function KnowledgeLibraryPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const { categories, stories, isLoading, error } = useKnowledgeList();

  const filtered = useMemo(() => {
    if (activeCategory === "all") return stories;
    return (stories || []).filter((s) => s.category === activeCategory);
  }, [stories, activeCategory]);

  const featured   = (filtered || []).filter((s) => s.isFeatured);
  const rest       = (filtered || []).filter((s) => !s.isFeatured);
  const totalCount = stories?.length ?? 0;

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero — deliberately smaller than Opportunities hero */}
        <section className="relative overflow-hidden border-b border-surface-200 bg-white">
          <div className="pointer-events-none absolute inset-0 fursa-grid-bg opacity-40" aria-hidden />
          <div className="relative mx-auto max-w-5xl px-4 pb-8 pt-8 sm:px-6 sm:pt-10 lg:px-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-surface-200 bg-surface-50 px-3 py-1 text-[10.5px] font-semibold uppercase tracking-widest text-intel-800">
              <Library className="h-3 w-3 text-intel-700" strokeWidth={2} aria-hidden />
              Executive Learning Library
            </div>

            <h1 className="arabic-headline mt-4 text-[26px] font-bold leading-[1.16] tracking-tightest text-ink-900 text-balance sm:text-[36px] sm:leading-[1.1] lg:text-[44px]">
              مكتبة المعرفة
            </h1>
            <p className="arabic-body mt-3 max-w-[640px] text-[14.5px] leading-[1.75] text-ink-500 text-pretty sm:mt-4 sm:text-[16px]">
              قصص موجزة موثّقة من شخصيات وشركات — لِنتعلّم مبدأ عملياً في اتخاذ القرار
              الاستثماري كل أسبوع. كل قصّة أقلّ من دقيقتين قراءة، بمصادر يمكن التحقّق منها.
            </p>
          </div>
        </section>

        {/* Category tabs */}
        <section className="border-b border-surface-200 bg-white">
          <div className="mx-auto flex max-w-5xl items-center gap-1 overflow-x-auto px-4 no-scrollbar sm:px-6 lg:px-8">
            <CategoryTab
              testId="knowledge-cat-all"
              label="الكل"
              count={totalCount}
              active={activeCategory === "all"}
              onClick={() => setActiveCategory("all")}
            />
            {(categories || []).map((c) => (
              <CategoryTab
                key={c.key}
                testId={`knowledge-cat-${c.key}`}
                label={c.label}
                count={c.count}
                active={activeCategory === c.key}
                onClick={() => setActiveCategory(c.key)}
              />
            ))}
          </div>
        </section>

        {/* Body */}
        <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
          {isLoading && <LoadingShell />}
          {error && !isLoading && (
            <p
              data-testid="knowledge-error"
              className="rounded-xl border border-risk-100 bg-risk-50 p-6 text-sm text-risk-700"
            >
              تعذّر تحميل المكتبة. حاول تحديث الصفحة.
            </p>
          )}
          {!isLoading && !error && filtered.length === 0 && (
            <p
              data-testid="knowledge-empty"
              className="rounded-xl border border-surface-200 bg-white p-8 text-center text-sm text-ink-500"
            >
              لا توجد قصص ضمن هذا التصنيف حالياً.
            </p>
          )}
          {!isLoading && featured.length > 0 && (
            <div className="mb-8 grid gap-5 md:grid-cols-2">
              {featured.map((s) => <FeaturedStoryCard key={s.id} story={s} />)}
            </div>
          )}
          {!isLoading && rest.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((s) => <StoryCard key={s.id} story={s} />)}
            </div>
          )}

          {/* Roadmap — what's coming next.
              Kept restrained: neutral chips, no color noise, no CTA.
              This tells the executive reader "we're building an
              expanding taxonomy" without cluttering the surface. */}
          {!isLoading && COMING_SOON_CATEGORIES.length > 0 && activeCategory === "all" && (
            <ComingSoonBand />
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ─── Primitives ─────────────────────────────────────────────── */

const CategoryTab = ({ label, count, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`relative inline-flex items-center gap-2 whitespace-nowrap px-4 py-3.5 text-sm font-medium transition-colors ${
      active
        ? "text-intel-900 after:absolute after:inset-x-4 after:-bottom-px after:h-[2px] after:rounded-full after:bg-intel-900"
        : "text-ink-500 hover:text-ink-900"
    }`}
    aria-pressed={active}
  >
    {label}
    <span
      dir="ltr"
      className="num rounded-full bg-surface-100 px-1.5 py-0.5 font-mono text-[10.5px] font-semibold tabular-nums text-ink-500"
    >
      {count}
    </span>
  </button>
);

const FeaturedStoryCard = ({ story }) => {
  const Icon = CATEGORY_ICON[story.category] ?? User;
  return (
    <Link
      to={`/knowledge/${story.slug}`}
      data-testid={`knowledge-card-featured-${story.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-surface-200 bg-white shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
    >
      <ImagePlaceholder category={story.category} coverImageUrl={story.coverImageUrl} tall />
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-fursa-50 px-2 py-0.5 text-[10px] font-semibold text-fursa-700">
            <Star className="h-3 w-3" strokeWidth={2} aria-hidden />
            مميّزة
          </span>
          <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-widest text-intel-800">
            <Icon className="h-3 w-3 text-intel-700" strokeWidth={2} aria-hidden />
            {CATEGORY_LABEL[story.category]}
          </span>
        </div>

        <p className="mt-3 text-[12px] font-medium text-ink-500">{story.subjectName}</p>
        <h3 className="arabic-headline mt-1 text-[18px] font-semibold leading-[1.32] tracking-tight text-ink-900 text-balance group-hover:text-intel-900 sm:text-[20px]">
          {story.title}
        </h3>
        {story.intro && (
          <p className="arabic-body mt-3 text-[13.5px] leading-relaxed text-ink-700 line-clamp-3 text-pretty">
            {story.intro}
          </p>
        )}

        <div className="mt-auto flex items-center justify-between border-t border-surface-100 pt-4 text-[12px]">
          <span className="inline-flex items-center gap-1.5 text-ink-500">
            <Clock className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
            <span dir="ltr" className="num font-mono tabular-nums">{story.readTimeMinutes}</span> د قراءة
          </span>
          <span className="inline-flex items-center gap-1 font-medium text-ink-700 group-hover:text-intel-900">
            اقرأ المزيد
            <ChevronLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-1" strokeWidth={1.75} aria-hidden />
          </span>
        </div>
      </div>
    </Link>
  );
};

const StoryCard = ({ story }) => {
  const Icon = CATEGORY_ICON[story.category] ?? User;
  return (
    <Link
      to={`/knowledge/${story.slug}`}
      data-testid={`knowledge-card-${story.slug}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-surface-200 bg-white shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
    >
      <ImagePlaceholder category={story.category} coverImageUrl={story.coverImageUrl} />
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-widest text-intel-800">
          <Icon className="h-3 w-3 text-intel-700" strokeWidth={2} aria-hidden />
          {CATEGORY_LABEL[story.category]}
        </div>
        <p className="mt-2 text-[11.5px] font-medium text-ink-500">{story.subjectName}</p>
        <h3 className="arabic-headline mt-1 text-[15.5px] font-semibold leading-[1.4] tracking-tight text-ink-900 line-clamp-2 group-hover:text-intel-900">
          {story.title}
        </h3>
        {story.intro && (
          <p className="mt-2 text-[13px] leading-relaxed text-ink-700 line-clamp-2 text-pretty">
            {story.intro}
          </p>
        )}

        <div className="mt-auto flex items-center justify-between border-t border-surface-100 pt-3.5 text-[11.5px]">
          <span className="inline-flex items-center gap-1 text-ink-500">
            <Clock className="h-3 w-3" strokeWidth={1.75} aria-hidden />
            <span dir="ltr" className="num font-mono tabular-nums">{story.readTimeMinutes}</span> د
          </span>
          <span className="inline-flex items-center gap-1 font-medium text-ink-700 group-hover:text-intel-900">
            اقرأ المزيد
            <ChevronLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-1" strokeWidth={1.75} aria-hidden />
          </span>
        </div>
      </div>
    </Link>
  );
};

const ImagePlaceholder = ({ category, coverImageUrl, tall }) => {
  const Icon = CATEGORY_ICON[category] ?? User;
  const [broken, setBroken] = useState(false);
  if (coverImageUrl && !broken) {
    return (
      <img
        src={coverImageUrl}
        alt=""
        aria-hidden
        className={`w-full object-cover ${tall ? "h-40 sm:h-44" : "h-32"}`}
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <div
      className={`relative flex items-center justify-center border-b border-surface-100 bg-surface-50 ${
        tall ? "h-28 sm:h-32" : "h-24"
      }`}
      aria-hidden
    >
      <div className="pointer-events-none absolute inset-0 fursa-grid-bg opacity-70" />
      <Icon className="relative h-6 w-6 text-intel-300" strokeWidth={1.5} />
    </div>
  );
};

const LoadingShell = () => (
  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="fursa-skeleton h-56 rounded-xl" />
    ))}
  </div>
);


/**
 * Coming-soon roadmap band.
 * Purely informative — telegraphs the expanding taxonomy to the
 * executive reader while keeping the current surface uncluttered.
 */
const ComingSoonBand = () => (
  <aside
    data-testid="knowledge-coming-soon"
    className="mt-14 rounded-xl border border-dashed border-surface-300 bg-surface-50/60 p-5 sm:p-6"
  >
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <div>
        <p className="text-[10.5px] font-semibold uppercase tracking-widest text-intel-800">
          Roadmap
        </p>
        <h3 className="arabic-headline mt-1 text-[15.5px] font-semibold tracking-tight text-ink-900">
          تصنيفات قادمة في المكتبة
        </h3>
      </div>
      <span className="text-[11.5px] text-ink-500">
        نضيف قصصاً بمصادر موثّقة، لا محتوى تحريرياً عامّاً
      </span>
    </div>
    <div className="mt-4 flex flex-wrap gap-2">
      {COMING_SOON_CATEGORIES.map((c) => {
        const Icon = c.icon;
        return (
          <span
            key={c.key}
            className="inline-flex items-center gap-1.5 rounded-full border border-surface-200 bg-white px-3 py-1.5 text-[12px] text-ink-700"
          >
            <Icon className="h-3.5 w-3.5 text-intel-700" strokeWidth={1.75} aria-hidden />
            {c.label}
          </span>
        );
      })}
    </div>
  </aside>
);
